import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../core/hooks/use-language';
import { useTranslation } from '../../core/hooks/use-translation';
import type { PortalProfile } from '../../core/models/portal-profile';
import { Avatar } from '../../ui/avatar';
import { CalendarIcon, CaseIcon } from '../../ui/portal-icons';

/**
 * The band at the top of the portal: who is signed in, and what is open.
 *
 * **It replaces a bare `h1` reading "Personal cabinet".** A customer arriving here has one question
 * — is anything happening on my file — and the old heading answered it only after they had read
 * three panels. The two counts are that answer at a glance, and they are counts rather than a
 * status because a customer with two cases has two answers and a single line would have to pick one.
 *
 * The name is the profile's, not the session's: the session copy is a display cache that survives a
 * reload, and this screen has already loaded the real record by the time it draws.
 */
export function PortalGreeting({
    profile,
    caseCount,
    appointmentCount
}: {
    profile: PortalProfile | null;
    caseCount: number;
    appointmentCount: number;
}) {
    const { t } = useTranslation();
    const language = useLanguage();

    const name = profile === null ? '' : [profile.firstName, profile.lastName].filter(Boolean).join(' ');

    return (
        <section className="overflow-hidden rounded-3xl bg-gradient-to-br from-brand-700 via-brand-600 to-brand-500 text-white shadow-lg shadow-brand-900/15">
            <div className="flex flex-wrap items-center gap-4 px-5 py-6 sm:gap-5 sm:px-8 sm:py-7">
                <Avatar
                    name={name}
                    className="size-12 bg-white/15 text-base text-white ring-1 ring-white/25 sm:size-14 sm:text-lg"
                />

                {/* The name wraps on a telephone: an ellipsis there cut a double surname in half. */}
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-white/80">{t('portal.welcome')}</p>
                    <h1 className="text-xl font-extrabold break-words sm:truncate sm:text-3xl">
                        {name.length > 0 ? name : t('portal.title')}
                    </h1>
                </div>

                <Link
                    to={`/${language}/book`}
                    className="w-full rounded-xl bg-white px-4 py-2.5 text-center text-sm font-bold text-brand-700 shadow-sm transition hover:bg-brand-50 sm:w-auto"
                >
                    {t('portal.book_appointment')}
                </Link>
            </div>

            {/* Border rather than a second block: the counts belong to the band, not under it. */}
            <div className="grid grid-cols-2 gap-px border-t border-white/15 bg-white/15">
                <Count icon={<CaseIcon className="size-5" />} value={caseCount} label={t('portal.cases')} />
                <Count
                    icon={<CalendarIcon className="size-5" />}
                    value={appointmentCount}
                    label={t('portal.appointments')}
                />
            </div>
        </section>
    );
}

function Count({ icon, value, label }: { icon: ReactNode; value: number; label: string }) {
    return (
        <div className="flex items-center gap-2.5 bg-gradient-to-br from-brand-700/95 to-brand-600/95 px-4 py-3.5 sm:gap-3 sm:px-8 sm:py-4">
            <span className="text-white/70">{icon}</span>
            <span className="text-2xl font-extrabold tabular-nums">{value}</span>
            <span className="text-sm text-white/80">{label}</span>
        </div>
    );
}
